import { Link } from 'react-router-dom';
import { CalendarDays, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { usePublicProducerEvents } from '@/hooks/usePublicProducerEvents';
import { PublicEventCard } from '@/components/public/PublicEventCard';
import { useCoverImageUrl } from '@/hooks/useMediaAssets';
import { formatTimingBadge } from '@/lib/events/eventCardUtils';
import { useTranslate } from '@/i18n';
import type { PublicProducerEvent } from '@/types/publicEvent';

interface ProducerEventsProps {
  producerId?: string;
  producerSlug?: string;
  producerName?: string;
  producerCity?: string;
  title?: string;
  maxItems?: number;
}

export function ProducerEvents({ producerId, producerSlug, producerName, producerCity, title, maxItems = 3 }: ProducerEventsProps) {
  const t = useTranslate();
  const heading = title ?? t("producer.events.heading");
  const { data: events, isLoading } = usePublicProducerEvents(producerId);
  // Producer cover as fallback for activities without their own image
  const producerCoverUrl = useCoverImageUrl('producer', producerId ?? null, 640);

  if (!producerId || isLoading || !events || events.length === 0) return null;

  const visibleEvents = events.slice(0, maxItems);

  // Derive "Nästa: {label}" from the first event's date
  const firstEvent = visibleEvents[0];
  const nextLabel = firstEvent
    ? formatTimingBadge(
        (firstEvent.timing_mode || 'date_time') as 'date_time' | 'open' | 'anytime' | 'season',
        firstEvent.next_occurrence_at || firstEvent.start_date,
        firstEvent.outing_tags,
        firstEvent.end_date,
      )
    : null;

  return (
    <section className="border-t border-border/30 pt-8">
      <div className="flex items-center gap-2 mb-1">
        <div className="flex items-center justify-center w-8 h-8 rounded-full bg-primary/10 text-primary">
          <CalendarDays className="h-4 w-4" />
        </div>
        <h2 className="font-display text-lg text-foreground">
          {heading}
        </h2>
      </div>

      {/* Subtle "Nästa: ..." microline */}
      {nextLabel && (
        <p className="text-xs text-muted-foreground ml-10 mb-4">
          Nästa: {nextLabel}
        </p>
      )}
      {!nextLabel && <div className="mb-4" />}

      {/* Mobile: stack, Desktop: grid */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {visibleEvents.map((event) => (
          <ProducerEventCardWrapper
            key={event.event_id}
            event={event}
            producerName={producerName}
            producerCity={producerCity}
            fallbackCoverUrl={producerCoverUrl}
          />
        ))}
      </div>

      {events.length > maxItems && (
        <div className="text-center mt-6">
          <Button variant="outline" asChild>
            <Link to={`/producer/${producerSlug || producerId}/events`}>
              {t("producer.events.viewAll")}
              <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
          </Button>
        </div>
      )}
    </section>
  );
}

/** Thin wrapper that maps PublicProducerEvent → PublicEventCard props */
function ProducerEventCardWrapper({
  event,
  producerName,
  producerCity,
  fallbackCoverUrl,
  className,
}: {
  event: PublicProducerEvent;
  producerName?: string;
  producerCity?: string;
  fallbackCoverUrl?: string | null;
  className?: string;
}) {
  const coverUrl = useCoverImageUrl('event', event.event_id, 640);

  const timingMode = (event.timing_mode || 'date_time') as 'date_time' | 'open' | 'anytime' | 'season';

  return (
    <PublicEventCard
      event={{
        id: event.event_id,
        title: event.title,
        startDate: event.next_occurrence_at || event.start_date,
        endDate: event.end_date,
        timingMode,
        outingTags: event.outing_tags,
        producerName: producerName ?? null,
        producerCity: producerCity,
        location: event.venue_text ?? event.location ?? null,
        eventType: (event.event_type as 'event' | 'course') || 'event',
        includesFarmSale: event.includes_farm_sale ?? false,
        includesFarmSaleAlcohol: event.includes_farm_sale_alcohol ?? false,
        occurrencesCount: event.occurrences_count ?? 0,
      }}
      coverImageUrl={coverUrl}
      fallbackCoverUrl={fallbackCoverUrl}
      showTypeBadge={false}
      onClick={() => {
        window.location.href = `/event/${event.event_id}`;
      }}
      className={className}
    />
  );
}
